import { useApproveBranch, useUnApproveBranch } from '@festina-life/api'
import { useSettingsContext } from '@festina-life/components'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useBaseBranchWorkspaces } from '../../hooks/useBaseBranchWorkspaces'
import { useFetchBranchChangesets } from '../../uiModels/api/changesets/useFetchBranchChangesets'
import {
  fetchAllBranchChangesetsKeyPrefix,
  fetchAllWorkspaceBranchChangesetsKeyPrefix,
} from '../../uiModels/api/keys'
import type { UserActionData } from '../../uiModels/types'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { useOperationsContext } from '../../workspace/operationsContext'
import { OperationApprovalStatus } from './OperationApprovalStatus'

type Props = {
  changesetKey: Optional<string>
  operationKey: Optional<string>
  actions: UserActionData[]
}

export const OperationApproval = ({
  changesetKey,
  operationKey,
  actions,
}: Props) => {
  const { branchKey } = useOperationsContext()
  const isPersonaEditor = useIsPersonaEditor()
  const { data: currentBranchChangesets } = useFetchBranchChangesets({
    enabled: !!branchKey && isPersonaEditor,
    branchKey,
    includeBaseChangesets: false,
    shouldIncludeExtrinsicParameterValues: false,
  })
  const [isApprovalInProgress, setIsApprovalInProgress] = useState(false)
  const queryClient = useQueryClient()
  const allWorkspaces = useBaseBranchWorkspaces(branchKey)

  const { mutateAsync: approveBranch } = useApproveBranch()
  const { mutateAsync: unApproveBranch } = useUnApproveBranch()
  const {
    authState: { accounts },
  } = useSettingsContext()
  const currentUserId = accounts[0].localAccountId

  const isChangesetKeyInCurrentBranch =
    !!currentBranchChangesets?.branchChangesets
      ?.filter(notEmpty)
      .find(
        (branchChangeset) =>
          branchChangeset.changeset?.changesetKey &&
          branchChangeset.changeset?.changesetKey === changesetKey,
      )

  const handleToggleApproval =
    (operation: typeof approveBranch | typeof unApproveBranch) => async () => {
      assertNotEmpty(branchKey)
      assertNotEmpty(changesetKey)
      assertNotEmpty(operationKey)

      setIsApprovalInProgress(true)

      await operation({
        branchKey,
        workspaceNames: allWorkspaces.map(({ name }) => name).filter(notEmpty),
        changesetKey,
        operationKeys: [operationKey],
      })

      await queryClient.invalidateQueries({
        queryKey: fetchAllBranchChangesetsKeyPrefix({
          componentName: branchKey.server,
          branchName: branchKey.name,
          isFromConfigurationServer: true,
        }),
      })

      await queryClient.invalidateQueries({
        queryKey: fetchAllWorkspaceBranchChangesetsKeyPrefix({
          componentName: branchKey.server,
          baseBranchName: branchKey.name,
        }),
      })

      setIsApprovalInProgress(false)
    }

  const creationAction = actions.find((action) => action.action === 'create')
  const approvalAction = actions.find((action) => action.action === 'approve')

  return (
    isPersonaEditor && (
      <OperationApprovalStatus
        actions={actions}
        canApproveOperation={
          !approvalAction?.actionTime &&
          currentUserId !== creationAction?.userId &&
          isChangesetKeyInCurrentBranch
        }
        canUnApproveOperation={
          !!approvalAction?.actionTime && isChangesetKeyInCurrentBranch
        }
        onApprove={handleToggleApproval(approveBranch)}
        onUnApprove={handleToggleApproval(unApproveBranch)}
        isMutating={isApprovalInProgress}
      />
    )
  )
}
